import { useState, Fragment, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { Menu as MenuIcon, X, ChevronDown, UserCircle, LogOut } from "lucide-react";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CustomCaptureAmountModal from "../modal/CustomCaptureAmountModal";
import { performLogout } from "../../store/thunk/authThunk";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/reducer/authSlice";
import { fetchDeliveryPartnersList } from "../../store/thunk/menuThunk";
import { fetchOrderCountByPickupDate } from "../../store/thunk/ordersThunk";

const AdminHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCustomCaptureModal, setShowCustomCaptureModal] = useState(false);
  const [orderCounts, setOrderCounts] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(selectUser);
  const { deliveryPartnersList = [] } = useSelector((state) => state.menus);
  const [deliveryPartnerNavigation, setDeliveryPartnerNavigation] = useState([
    {
      name: "Place Order",
      children: [
        {
          name: "Delivery Partner",
          href: "/orders-list?type=delivery-partner",
        },
        { name: "Delivery Status", href: "/order-tracking-list" },
      ],
    },
    { name: "Orders list", href: "/partner-orders" },
    // { name: "Customer list", href: "/" },
    { name: "My Order", href: "/orders" },
    { name: "Payment Details", href: "/payment-details" },
  ]);

  const [defaultNavigation, setDefaultNavigation] = useState([
    {
      name: "Orders List",
      children: [
        { name: "Default", href: "/orders-list?type=default", group: "orders" },
        { name: "Qurbani", href: "/orders-list?type=qurbani", group: "orders" },
        {
          name: "Delivery Partner",
          href: "/orders-list?type=delivery-partner",
          group: "orders",
          groupEnd: true,
        },
        {
          name: "Order Validation List",
          href: "/order-validation-list",
          group: "validation"
        },
        {
          name: "Zelle Payment List",
          href: "/zelle-payment-list",
          group: "validation",
          groupEnd: true,
        },
        {
          name: "Custom Capture Amount",
          href: "/amount-list",
          action: "modal",
        },
        { name: "Cut off Time", href: "/add-cut-off-time", divider: true },
        { name: "Product Summary", href: "/order-product-summary", divider: true },
        { name: "Drivers Assignment", href: "/driver-assignment", divider: true },
        { name: "Delivery Status", href: "/order-tracking-list", divider: true },
      ],
    },

    { name: "Products", href: "/product-list" },
    { name: "Notifications List", href: "/notification-list" },
    { name: "Users", href: "/user-management" },
    {
      name: "Delivery Partners",
      children: [
        { name: "Partners List", href: "/delivery-partners-management" },
      ],
    },
    { name: "Qurbani Feedback", href: "/qurbani-feedback" },
    { name: "Print Label", href: "/custom-print-label" },
  ]);

  // Fetch delivery partners list on component mount
  useEffect(() => {
    dispatch(fetchDeliveryPartnersList());
  }, [dispatch]);

  // Fetch order counts by pickup date
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    dispatch(fetchOrderCountByPickupDate(today))
      .unwrap()
      .then((response) => {
        if (response?.status === 1 && response?.data) {
          setOrderCounts(response.data);
        }
      })
      .catch(() => {
        // Silently handle error
      });
  }, [dispatch]);

  // Update navigation for role 6 users (delivery partners)
  useEffect(() => {
    if (user?.roles === 6 && deliveryPartnersList?.length > 0) {
      // Create children links for each delivery partner
      const partnerLinks = deliveryPartnersList.map((partner) => ({
        name: partner.name,
        href: `/delivery-partners/${partner.short_name}`,
      }));

      // Update navigation
      setDeliveryPartnerNavigation((prev) =>
        prev.map((item) =>
          item.name === "Place Order"
            ? {
                ...item,
                children: [
                  ...partnerLinks,
                ],
              }
            : item
        )
      );
    }
  }, [deliveryPartnersList, user]);

  // Update navigation for admin users (dynamically populate delivery partners)
  useEffect(() => {
    if (user?.roles !== 6 && deliveryPartnersList?.length > 0) {
      // Create children links for each delivery partner
      const partnerLinks = deliveryPartnersList.map((partner) => ({
        name: partner.name,
        href: `/delivery-partners/${partner.short_name}`,
      }));

      // Update default navigation with delivery partners
      setDefaultNavigation((prev) =>
        prev.map((item) =>
          item.name === "Delivery Partners"
            ? {
                ...item,
                children: [
                  { name: "Partners List", href: "/delivery-partners-management", groupEnd: true },
                  ...partnerLinks,
                ],
              }
            : item
        )
      );
    }
  }, [deliveryPartnersList, user]);

  const isActive = (href) => location.pathname === href;
  const isParentActive = (children = []) => {
    return children.some((child) => location.pathname.startsWith(child.href));
  };

  const handleMenuItemClick = (item) => {
    if (item.action === "modal" && item.name === "Custom Capture Amount") {
      setShowCustomCaptureModal(true);
    } else {
      // Open in same window
      navigate(item.href);
    }
  };

  const handleCustomCaptureModalClose = () => {
    setShowCustomCaptureModal(false);
  };
  const navigation =
    user?.roles === 6 ? deliveryPartnerNavigation : defaultNavigation;
  return (
    <header className="relative inset-x-0 top-0 bg-primary-100 text-white z-50 ">
      <nav
        className="flex items-center justify-between container  px-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link
            to={"/"}
            className="-m-1.5 p-1.5 my-2"
          >
            <img
              src="https://images.halalfull.com/assets/img/logo-white.png"
              alt="Logo"
              className="h-12"
            />
          </Link>
        </div>
        <div className="flex space-x-2 items-center">
          {orderCounts && (
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-md text-sm hover:bg-white/20 transition-colors">
                <span className="font-semibold">{orderCounts.total_count}</span>
                <span className="text-gray-300">Orders</span>
                <ChevronDown className="w-4 h-4 text-gray-300" />
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-2 shadow-lg data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500">Pickup Date</p>
                  <p className="text-sm font-semibold text-gray-900">{orderCounts.pickup_date}</p>
                </div>
                <div className="px-4 py-2">
                  <div className="flex justify-between items-center py-1">
                    <span className="text-sm text-gray-600">Delivery Partner</span>
                    <span className="text-sm font-semibold text-gray-900">{orderCounts.delivery_partner_count}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-sm text-gray-600">Default</span>
                    <span className="text-sm font-semibold text-gray-900">{orderCounts.default_count}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-t border-gray-100 mt-1 pt-2">
                    <span className="text-sm font-medium text-gray-700">Total</span>
                    <span className="text-sm font-bold text-primary-100">{orderCounts.total_count}</span>
                  </div>
                </div>
              </MenuItems>
            </Menu>
          )}
          <div className="lg:flex mr-4 lg:flex-1 lg:justify-end">
            <Menu as="div" className="relative">
              <div>
                <MenuButton className="relative flex rounded-full items-center text-sm">
                  <span className="absolute -inset-1.5" />
                  <div className="flex items-center">
                    <h2 className="hidden sm:block font-semibold">
                      {user?.roles === 1
                        ? "Welcome Admin"
                        : user?.roles === 6
                        ? user?.name
                          ? `Welcome, ${user.name}`
                          : "Welcome Partner"
                        : "Welcome User"}
                    </h2>
                    <ChevronDown className="hidden sm:block w-4 h-4 text-gray-300" />
                    <div className="flex items-center space-x-1">
                      <UserCircle className="w-8 h-8" />
                    </div>
                  </div>
                </MenuButton>
              </div>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg  data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                <MenuItem>
                  <button
                    onClick={() => navigate("/billing-address")}
                    className="block px-4 py-2 text-sm text-gray-700 "
                  >
                    Your Profile
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    onClick={() => navigate("/password-settings")}
                    className="block px-4 py-2 text-sm text-gray-700 "
                  >
                    Settings
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    onClick={() => {
                      dispatch(performLogout()).then(() => {
                        navigate("/");
                      });
                    }}
                    className="block px-4 py-2 text-sm text-gray-700 "
                  >
                    Sign out
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 "
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuIcon className="size-6" />
            </button>
          </div>
        </div>
      </nav>
      <div className="bg-white shadow-md">
        <div className="container">
          <div className="hidden lg:flex lg:gap-x-3 w-full pt-3">
            {navigation.map((item) =>
              item.children ? (
                <Menu as="div" className="relative" key={item.name}>
                  <MenuButton
                    className={`flex items-center px-4 py-3 whitespace-nowrap gap-1 border-b-2 text-sm font-semibold outline-none 
                                        ${
                                          isParentActive(item.children)
                                            ? "text-primary-text font-semibold border-primary-text"
                                            : "text-primary-500 border-b-transparent"
                                        }`}
                  >
                    {item.name}
                    <ChevronDown className="size-4" />
                  </MenuButton>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <MenuItems className="absolute mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg  z-50 outline-none border border-neutral-200">
                      <div className="">
                        {item.children.map((subItem, index) => (
                          <Fragment key={subItem.name}>
                            {/* Show divider before this item if divider is true */}
                            {subItem.divider && index > 0 && (
                              <div className="border-t border-gray-200 my-1 mx-3"></div>
                            )}
                            <MenuItem>
                              {() => (
                                <button
                                  onClick={() => handleMenuItemClick(subItem)}
                                  className="block px-4 py-2 text-sm hover:bg-primary-100 hover:text-white w-full text-left text-primary-500 last:rounded-b-md first:rounded-t-md"
                                >
                                  {subItem.name}
                                </button>
                              )}
                            </MenuItem>
                            {/* Show divider after this item if groupEnd is true */}
                            {subItem.groupEnd && index < item.children.length - 1 && (
                              <div className="border-t border-gray-200 my-2 mx-3"></div>
                            )}
                          </Fragment>
                        ))}
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
              ) : (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={`text-sm font-semibold text-left w-fit border-b-2 px-4 py-3
                                        ${
                                          isActive(item.href)
                                            ? "text-red-500 font-semibold border-primary-text"
                                            : "text-primary-500 border-transparent "
                                        }`}
                >
                  {item.name}
                </button>
              )
            )}
          </div>
        </div>
      </div>
      {/* Mobile Dialog */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between bg-primary-100 px-6">
            <button
              onClick={() => navigate("/")}
              className="-m-1.5 p-1.5"
            >
              <img
                className="h-20 w-20"
                src="https://images.halalfull.com/dev/assets/img/logo.svg"
                alt="Halalfull - Buy Halal Food Online California"
              />
            </button>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 "
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <X className="size-6 text-white" />
            </button>
          </div>
          <div className="mt-6 flow-root h-full flex flex-col">
            <div className="flex-1 px-5 overflow-y-auto">
              <div className="space-y-2 py-6">
                {navigation.map((item) =>
                  item.children ? (
                    <Menu as="div" className="relative w-full" key={item.name}>
                      <MenuButton
                        className={`flex w-full justify-between items-center px-4 py-3 text-sm font-semibold rounded-lg border border-gray-200
                                    ${isParentActive(item.children) ? "text-primary-text bg-red-50 border-primary-100" : "text-primary-500 bg-white hover:bg-gray-50"}`}>
                        {item.name}
                        <ChevronDown className="size-4" />
                      </MenuButton>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        <MenuItems className="mt-2 w-full rounded-lg bg-white shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto">
                          {item.children.map((subItem, index) => (
                            <Fragment key={subItem.name}>
                              {/* Show divider before this item if divider is true */}
                              {subItem.divider && index > 0 && (
                                <div className="border-t border-gray-200 my-1 mx-3"></div>
                              )}
                              <MenuItem>
                                {() => (
                                  <button
                                    onClick={() => {
                                      handleMenuItemClick(subItem);
                                      setMobileMenuOpen(false);
                                    }}
                                    className={`block px-4 py-2.5 text-sm w-full text-left
                                    ${
                                        isActive(
                                        subItem.href
                                        )
                                        ? "text-primary-text font-semibold bg-red-50"
                                        : "text-primary-500 hover:bg-primary-100 hover:text-white"
                                    }`}
                                  >
                                    {subItem.name}
                                  </button>
                                )}
                              </MenuItem>
                              {/* Show divider after this item if groupEnd is true */}
                              {subItem.groupEnd && index < item.children.length - 1 && (
                                <div className="border-t border-gray-200 my-2 mx-3"></div>
                              )}
                            </Fragment>
                          ))}
                        </MenuItems>
                      </Transition>
                    </Menu>
                  ) : (
                    <div key={item.name}>
                      <button
                        onClick={() => {
                          navigate(item.href);
                          setMobileMenuOpen(false);
                        }}
                        className={`text-sm font-semibold text-left w-full px-4 py-3 rounded-lg border border-gray-200
                        ${
                            isActive(item.href)
                            ? "text-primary-text bg-red-50 border-primary-100"
                            : "text-primary-500 bg-white hover:bg-gray-50"
                        }`}
                      >
                        {item.name}
                      </button>
                    </div>
                  )
                )}
                <button
                  onClick={() => {
                    dispatch(performLogout()).then(() => {
                      navigate("/");
                      setMobileMenuOpen(false);
                    });
                  }}
                  className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-white rounded-md flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>

      {/* Custom Capture Amount Modal */}
      <CustomCaptureAmountModal
        open={showCustomCaptureModal}
        setOpen={handleCustomCaptureModalClose}
      />
    </header>
  );
};

export default AdminHeader;
